import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./Appoint.css";
import Api from "../../Data/Api";

const serviceCategories = [
  {
    category: "Home & Visiting Services",
    items: [
      "Doctor Home Visit",
      "Hospital at Home Care",
      "Nursing & Bedside Medical Care",
      "Patient Follow-up & Chronic Medicare",
      "Visiting Doctors (Institution & Individual)",
      "Remote Patient Monitoring",
    ],
  },
  {
    category: "Maternal & Child Health",
    items: [
      "Midwife Consultation & Antenatal Care",
      "Virtual Doctor & Midwife Consultations",
      "Family Planning",
      "Paediatrician",
      "Gynaecologist",
    ],
  },
  {
    category: "Corporate & Community Health",
    items: [
      "Corporate Staff Medical Assessment",
      "School Student Medical Examination",
      "Community Outreach",
      "Medical Camp",
      "Healthcare Campaign",
      "Environmental Healthcare Consultancy",
    ],
  },
  {
    category: "Specialist & Surgical Services",
    items: [
      "General Doctor Consult",
      "Elective Surgical Services",
      "Surgeon",
      "Dental Care Services",
      "Referral & Link to Tertiary Care",
      "Ambulance Services",
    ],
  },
  {
    category: "Mental Health",
    items: [
      "Mental Health Services",
      "Psychiatric Healthcare",
      "Psychologist Services",
    ],
  },
  {
    category: "Laboratory, Pharmacy & Imaging",
    items: [
      "Laboratory Sample Pick-up",
      "Pharmacy",
      "Medicine Delivery",
      "Medicine Refill",
      "Ultrasound Scan",
      "X-ray",
      "CT Scan",
      "MRI",
      "Angiogram",
      "Equipment Purchase",
    ],
  },
];

const emptyAppointment = () => ({
  name: "", age: "", gender: "", address: "", tribe: "", occupation: "", religion: "", level: "",
  phone: "", email: "", weight: "", height: "", mass: "", smoke: "", alcohol: "", concern: "",
  package: "", otherconcern: "", visittime: "", visidate: "", paymentmode: "", ref: "", feedback: "",
  udrug: "", anymedi: "", unknowndrug: "", anypastsurvery: "", unknownfamily: "", unknownchronic: "",
  cost: "", qty: "", action: "Confirming Cost", PaidId: "", PaidAmount: "",
  Tx: Math.floor(Math.random() * (500000000 - 100000000 + 1)) + 100000000,
});

function FormField({ label, name, children, className = "" }) {
  return (
    <div className={`appointment-field ${className}`}>
      <label htmlFor={`appointment-${name}`}>{label}</label>
      {children}
    </div>
  );
}

export default function Appoint() {
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState(emptyAppointment);
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = ({ target: { name, value } }) => {
    setAppointment((current) => ({ ...current, [name]: value }));
  };

  const input = (name, props = {}) => (
    <input
      id={`appointment-${name}`}
      name={name}
      value={appointment[name]}
      onChange={updateField}
      {...props}
    />
  );

  const submitAppointment = async (event) => {
    event.preventDefault();
    if (!agreed) {
      Swal.fire("Confirmation required", "Please confirm that your information is correct before booking.", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      await Api.createAppointments(appointment);
      await Api.sendAppointmentNotification(appointment);
      await Swal.fire("Request sent", "Our care team will contact you to confirm your appointment.", "success");
      navigate(`/Reciept/?${appointment.Tx}`);
    } catch (error) {
      console.error("Appointment submission failed:", error);
      Swal.fire("Unable to send request", "Please try again or contact us on +256 777 114 327.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="appointment-page">
      <div className="container">
        <header className="appointment-page-intro">
          <div className="appointment-kicker"><ion-icon name="calendar-outline"></ion-icon> Care booking</div>
          <h1>Book an Appointment</h1>
          <p>Share your details and preferred visit time. Our care team will review your request and contact you to confirm.</p>
        </header>

        <form className="appointment-form" onSubmit={submitAppointment}>
          <section className="appointment-form-section" aria-labelledby="patient-details">
            <div className="appointment-section-heading">
              <span>1</span><div><h2 id="patient-details">Patient details</h2><p>Tell us who needs care and how to reach them.</p></div>
            </div>
            <div className="appointment-form-grid">
              <FormField label="Full name" name="name">{input("name", { type: "text", autoComplete: "name", required: true, placeholder: "Patient's full name" })}</FormField>
              <FormField label="Age" name="age">{input("age", { type: "number", min: "0", max: "120", inputMode: "numeric", required: true, placeholder: "Age" })}</FormField>
              <FormField label="Sex / gender" name="gender"><select id="appointment-gender" name="gender" value={appointment.gender} onChange={updateField} required><option value="">Select an option</option><option value="Female">Female</option><option value="Male">Male</option><option value="Prefer not to say">Prefer not to say</option></select></FormField>
              <FormField label="Phone number" name="phone">{input("phone", { type: "tel", inputMode: "tel", autoComplete: "tel", pattern: "[0-9+() -]{7,20}", title: "Use 7 to 20 digits, spaces, +, brackets, or hyphens.", required: true, placeholder: "+256 700 000 000" })}</FormField>
              <FormField label="Email address" name="email">{input("email", { type: "email", autoComplete: "email", required: true, placeholder: "name@example.com" })}</FormField>
              <FormField label="Address / area" name="address">{input("address", { type: "text", autoComplete: "street-address", required: true, placeholder: "Kampala, neighbourhood, or landmark" })}</FormField>
              <FormField label="Occupation" name="occupation">{input("occupation", { type: "text", placeholder: "Optional" })}</FormField>
              <FormField label="Education level" name="level">{input("level", { type: "text", placeholder: "Optional" })}</FormField>
            </div>
          </section>

          <section className="appointment-form-section" aria-labelledby="service-request">
            <div className="appointment-section-heading"><span>2</span><div><h2 id="service-request">Service request</h2><p>Help us prepare the right kind of support.</p></div></div>
            <div className="appointment-form-grid">
              <FormField label="Service needed" name="package" className="appointment-field-wide"><select id="appointment-package" name="package" value={appointment.package} onChange={updateField} required><option value="">Choose a service</option>{serviceCategories.map((cat) => (<optgroup key={cat.category} label={cat.category}>{cat.items.map((service) => (<option key={service} value={service}>{service}</option>))}</optgroup>))}</select></FormField>
              <FormField label="What is your main concern today?" name="concern" className="appointment-field-wide"><textarea id="appointment-concern" name="concern" value={appointment.concern} onChange={updateField} rows="4" required placeholder="Describe the symptoms, concern, or care you need." /></FormField>
              <FormField label="Anything else the doctor should know?" name="otherconcern" className="appointment-field-wide"><textarea id="appointment-otherconcern" name="otherconcern" value={appointment.otherconcern} onChange={updateField} rows="3" placeholder="Optional additional information" /></FormField>
            </div>
          </section>

          <section className="appointment-form-section" aria-labelledby="health-history">
            <div className="appointment-section-heading"><span>3</span><div><h2 id="health-history">Health information</h2><p>Optional details that help the clinician understand your needs.</p></div></div>
            <div className="appointment-form-grid">
              <FormField label="Weight" name="weight">{input("weight", { type: "text", placeholder: "e.g. 65 kg" })}</FormField>
              <FormField label="Height" name="height">{input("height", { type: "text", placeholder: "e.g. 170 cm" })}</FormField>
              <FormField label="Do you smoke?" name="smoke"><select id="appointment-smoke" name="smoke" value={appointment.smoke} onChange={updateField}><option value="">Select an option</option><option value="Yes">Yes</option><option value="No">No</option></select></FormField>
              <FormField label="Do you take alcohol?" name="alcohol"><select id="appointment-alcohol" name="alcohol" value={appointment.alcohol} onChange={updateField}><option value="">Select an option</option><option value="Yes">Yes</option><option value="No">No</option></select></FormField>
              <FormField label="Current medicines" name="udrug" className="appointment-field-wide">{input("udrug", { type: "text", placeholder: "Optional" })}</FormField>
              <FormField label="Known drug or medicine allergies" name="unknowndrug" className="appointment-field-wide">{input("unknowndrug", { type: "text", placeholder: "Optional" })}</FormField>
              <FormField label="Past surgery, transfusion, or major accident" name="anypastsurvery" className="appointment-field-wide">{input("anypastsurvery", { type: "text", placeholder: "Optional" })}</FormField>
              <FormField label="Known chronic or family conditions" name="unknownchronic" className="appointment-field-wide">{input("unknownchronic", { type: "text", placeholder: "Optional" })}</FormField>
            </div>
          </section>

          <section className="appointment-form-section" aria-labelledby="visit-preference">
            <div className="appointment-section-heading"><span>4</span><div><h2 id="visit-preference">Visit preference</h2><p>Select a preferred date and time. We will confirm availability.</p></div></div>
            <div className="appointment-form-grid">
              <FormField label="Preferred date" name="visidate">{input("visidate", { type: "date", required: true })}</FormField>
              <FormField label="Preferred time" name="visittime">{input("visittime", { type: "time", required: true })}</FormField>
              <FormField label="Additional note" name="feedback" className="appointment-field-wide"><textarea id="appointment-feedback" name="feedback" value={appointment.feedback} onChange={updateField} rows="3" placeholder="Optional" /></FormField>
            </div>
          </section>

          <footer className="appointment-form-footer">
            <label className="appointment-consent" htmlFor="appointment-confirmation">
              <input id="appointment-confirmation" type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} required />
              <span>I confirm that the information provided is correct and I am ready to request an appointment.</span>
            </label>
            <button className="appointment-submit" type="submit" disabled={isSubmitting}>
              <ion-icon name={isSubmitting ? "hourglass-outline" : "send-outline"}></ion-icon>
              {isSubmitting ? "Sending request..." : "Request appointment"}
            </button>
          </footer>
        </form>
      </div>
    </main>
  );
}
